import React from 'react';

import { init } from '@fullstory/browser';
import '@tanstack/react-table';
import CacheBuster from 'react-cache-buster';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from 'react-query';
import { ReactQueryDevtools } from 'react-query/devtools';
import { StompSessionProvider } from 'react-stomp-hooks';

import App from 'components/App/app';

import mainPackage from '../package.json';

init({ orgId: 'o-1T5GBJ-na1' });

declare module 'react' {
    function forwardRef<T, P = {}>(
        render: (props: P, ref: React.Ref<T>) => React.ReactElement | null,
    ): (props: P & React.RefAttributes<T>) => React.ReactElement | null;
}

declare module '@tanstack/react-table' {
    interface ColumnMeta {
        isFixedSize?: boolean;
        columnLabel?: string;
        csvFormat?: (value, row) => unknown;
        filterPlaceholder?: string;
    }
}

const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            refetchOnWindowFocus: false,
            retry: false,
        },
    },
});

const url = new URL(document.URL);
const isProduction = process.env.NODE_ENV === 'production';
const webSocketUrl = isProduction
    ? window['ENV_VARIABLES']['REACT_APP_WEB_SOCKET_URL'].replace('$$_IP$$', url.hostname)
    : process.env.REACT_APP_WEB_SOCKET_URL;
const container = document.getElementById('app-root') as HTMLDivElement;
const root = createRoot(container);

async function enableMocking() {
    if (process.env.NODE_ENV !== 'development') {
        return;
    }

    const { worker } = await import('./mocks/browser');

    return worker.start({
        onUnhandledRequest: 'bypass', // or 'warn', or 'error'
    });
}

enableMocking().then(() => {
    root.render(
        <>
            {/* cache busting layer */}
            <CacheBuster
                currentVersion={mainPackage.version}
                isEnabled={isProduction} //If false, the library is disabled.
                isVerboseMode={true} //If true, the library writes verbose logs to console.
                reloadOnDowngrade={true}
                loadingComponent={null} //If not pass, nothing appears at the time of new version check.
            >
                {/* web socket layer */}
                <StompSessionProvider
                    url={`${webSocketUrl}/gs-guide-websocket`}
                    onStompError={(error) => {
                        console.log('Error connecting to stomp server', { error });
                    }}
                    heartbeatIncoming={0}
                    heartbeatOutgoing={0}
                    reconnectDelay={30000}
                    connectionTimeout={30000}
                    //All options supported by @stomp/stompjs can be used here
                >
                    {/* react query layer*/}
                    <QueryClientProvider client={queryClient}>
                        {/* main app component*/}
                        <App />
                        {/* react query dev tools layer - flower at bottom left corner visible only in development mode  */}
                        <ReactQueryDevtools initialIsOpen={false} />
                    </QueryClientProvider>
                </StompSessionProvider>
            </CacheBuster>
        </>,
    );
});
